import { useCallback, useEffect, useMemo, useState } from 'react'
import { Form } from 'antd'
import { CustomForm } from 'components/Forms/CustomForm'
import moment, { Moment } from 'moment'
import './styles.scss'
import { BTN_TXT, LABELS, TOOLTIP } from 'constants/txt'
import {
  fileNameFormatter,
  formatPhoneNumberToRequest,
  validateFileTypes,
  validateMessages
} from 'helper/common'
import { Typography, TYPOGRAPHY_WEIGHT } from 'components/Typography'
import { FileUpload } from 'components/FileUpload/FileUpload'
import { Attachment } from 'components/Attachment/Attachment'
import { useContractDetailsPopup } from 'features/ContractDetails/Providers/ContractDetailsPopupProvider'
import {
  ACCEPT_DOCUMENT_TYPES,
  UPLOAD_DOCUMENTS_CONFIG
} from 'features/ContractDetails/ExtendContract/constants'
import { useContractDetailsContext } from 'features/ContractDetails/Providers/ContractDetailsContextProvider'
import { Props, TAddAmendmentForm } from './types'
import { useHSTableContext } from 'features/ContractDetails/Facilities/Providers/HSTableProvider'
import { timelineMonthYearFormat } from 'components/Timelines/constants'
import { Datepicker } from 'components/Datepicker'
import { convertDateToRequestFormat } from 'utils/moment'
import { AddSignatorySelect } from '../../ContractDetailsGeneral/ContractDetailsDocuments/AddSignatoryPopup/AddSignatorySelect'
import cn from 'classnames'
import {
  getAvailableHSUsersForHospitalSignatory,
  getAvailableHSUsersForSignatory,
  getAvailableVendorUsersForHospitalSignatory,
  getAvailableVendorUsersForSignatory
} from '../../api'
import { useParams } from 'react-router-dom'
import { IAddHSAmendmentPayload } from '../types'
import { SIGNATORY_STATUS } from '../../constants'
import { notification } from '../../../../components/Notification'
import { TSignatoryUserOption } from '../../ContractDetailsGeneral/ContractDetailsDocuments/AddSignatoryPopup/types'
import { MAX_FILE_SIZE_30_MB, ROLES_NAMES } from '../../../../constants'
import { Switch } from '../../../../components/Switch'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { ReactComponent as ArrowRight16 } from '../../../../assets/svg/ArrowRight16.svg'
import { ReactComponent as ArrowLeft16 } from '../../../../assets/svg/ArrowBack16.svg'

export const AddAmendment = (props: Props) => {
  const {
    amendmentDocument,
    setAmendmentDocument,
    currentFile,
    setCurrentFile
  } = props

  const {
    state: { canAddFacilityLoPSignatories, details },
    actions: { addAmendmentDocumentAsync, uploadHSAmendmentDocumentAsync }
  } = useContractDetailsContext()
  const { addAmendmentPopup } = useContractDetailsPopup()

  const [form] = Form.useForm<TAddAmendmentForm>()

  const pricingEffectiveDate = Form.useWatch('pricing_effective_date', form)
  const initialCalculationDate = Form.useWatch('initial_calculation_date', form)
  const LoPSignatureRequired = Form.useWatch('is_signature_required', form)

  const { id } = useParams()

  const { refreshTableData } = useHSTableContext()
  const { selectedHSRow } = addAmendmentPopup.state.payload

  const [vendorSignatories, setVendorSignatories] = useState<
    TSignatoryUserOption[]
  >([])
  const [hsSignatories, setHsSignatories] = useState<TSignatoryUserOption[]>([])

  const selectedHSRowKey = selectedHSRow.uuid

  const editMode = !!selectedHSRow?.amendment_document

  const handleInitialCalculationDate = useCallback((value: Moment | null) => {
    if (!value) return moment()
    if (value.date() !== 1) {
      return value.add(1, 'month').startOf('month')
    } else return value.startOf('month')
  }, [])

  const handleUploadDocument = useCallback(
    (file: File[]) =>
      new Promise((res) => {
        setAmendmentDocument({
          file: file[0],
          uploadedAt: moment()
        })

        res(null)
      }),
    [setAmendmentDocument]
  )

  const handleDeleteDocument = useCallback(() => {
    if (editMode && currentFile) {
      setCurrentFile(null)
    } else setAmendmentDocument(null)
  }, [editMode, currentFile, setAmendmentDocument, setCurrentFile])

  const checkDateRange = useCallback(
    (date: Moment): boolean => {
      const startDate = moment(details.start_date)
      const endDate = moment(details.finish_date).subtract(1, 'days')
      return !date.isBetween(startDate, endDate, 'days', '[]')
    },
    [details.finish_date, details.start_date]
  )

  const checkDateRangeForInitial = useCallback(
    (date: Moment): boolean => {
      const startDate = pricingEffectiveDate
        ? moment(pricingEffectiveDate)
        : moment(details.start_date)
      const endDate = moment(details.finish_date).subtract(1, 'days')
      return !date.isBetween(
        handleInitialCalculationDate(startDate),
        endDate.startOf('month'),
        'days',
        '[]'
      )
    },
    [
      details.finish_date,
      details.start_date,
      pricingEffectiveDate,
      handleInitialCalculationDate
    ]
  )

  const isDocWasUpdated = useMemo(
    () => editMode && !currentFile && amendmentDocument,
    [editMode, amendmentDocument, currentFile]
  )

  const getReassignmentMessages = useCallback(
    (values) => {
      const messages = [] as string[]

      const signatoryNames = ['vendor_signatory', 'community_signatory']
      signatoryNames.forEach((signatory) => {
        if (
          values[signatory] &&
          selectedHSRow[signatory] &&
          values[signatory] !== selectedHSRow[signatory].signatory?.uuid &&
          values[signatory] !== selectedHSRow[signatory].email
        ) {
          const prevSignatory =
            selectedHSRow[signatory].email ??
            `${selectedHSRow[signatory].signatory?.first_name} ${selectedHSRow[signatory].signatory?.last_name}`
          const prevSignatoryRole = selectedHSRow[signatory].signatory?.role
            ? ROLES_NAMES[selectedHSRow[signatory].signatory?.role]
            : ''

          const options =
            signatory === 'vendor_signatory' ? vendorSignatories : hsSignatories

          let currentSignatory = values[signatory + '_phone_number']
            ? values[signatory]
            : options.find((vS) => vS.uuid === values[signatory])
          let currentSignatoryRole = ''
          if (currentSignatory.uuid && currentSignatory.role) {
            currentSignatoryRole = ROLES_NAMES[currentSignatory.role]
            currentSignatory = `${currentSignatory.first_name} ${currentSignatory.last_name}`
          }

          messages.push(
            `Signatory ${prevSignatoryRole} ${prevSignatory} has been changed to ${currentSignatoryRole} ${currentSignatory}.`
          )
        }
      })

      return messages
    },
    [selectedHSRow, vendorSignatories, hsSignatories]
  )

  const closeAmendmentPopup = useCallback(() => {
    addAmendmentPopup.actions.close()
    setAmendmentDocument(null)
    form.resetFields()
  }, [addAmendmentPopup.actions, setAmendmentDocument, form])

  const onChangeInitialCalculationDate = useCallback(
    (value: Moment | null) => {
      if (value)
        form.setFieldsValue({
          initial_calculation_date: value.startOf('month')
        })
    },
    [form]
  )

  const handleFinishForm = useCallback(
    async (values) => {
      if (
        editMode
          ? !currentFile && !amendmentDocument
          : amendmentDocument === null
      ) {
        return
      }
      const fileWasUploaded =
        (editMode && !currentFile && amendmentDocument) ||
        (!editMode && amendmentDocument)

      const hsAmendmentFile = fileWasUploaded
        ? await uploadHSAmendmentDocumentAsync(amendmentDocument.file)
        : null

      if ((hsAmendmentFile?.uuid ?? currentFile) && selectedHSRowKey) {
        let payload = {
          is_signature_required: Boolean(
            canAddFacilityLoPSignatories && LoPSignatureRequired
          ),
          pricing_effective_date: pricingEffectiveDate
            ? convertDateToRequestFormat(pricingEffectiveDate)
            : null,
          initial_calculation_date: values.initial_calculation_date
            ? convertDateToRequestFormat(values.initial_calculation_date)
            : null,
          file: fileWasUploaded
            ? hsAmendmentFile
            : selectedHSRow.amendment_document_uuid
        } as IAddHSAmendmentPayload
        if (canAddFacilityLoPSignatories && LoPSignatureRequired) {
          payload = {
            ...payload,
            community_signatory: {
              signatory: values.community_signatory_phone_number
                ? null
                : values.community_signatory,
              email: values.community_signatory_phone_number
                ? values.community_signatory
                : null,
              phone_number: formatPhoneNumberToRequest(
                values.community_signatory_phone_number
              )
            },
            vendor_signatory: {
              signatory: values.vendor_signatory_phone_number
                ? null
                : values.vendor_signatory,
              email: values.vendor_signatory_phone_number
                ? values.vendor_signatory
                : null,
              phone_number: formatPhoneNumberToRequest(
                values.vendor_signatory_phone_number
              )
            }
          }
        }
        if (selectedHSRow.hospital_level && selectedHSRow.hs_uuid) {
          await addAmendmentDocumentAsync(
            selectedHSRow.hs_uuid,
            selectedHSRow.uuid,
            payload,
            editMode
          )
        } else {
          await addAmendmentDocumentAsync(
            selectedHSRowKey.toString(),
            null,
            payload,
            editMode
          )
        }
        if (editMode) {
          const messages = getReassignmentMessages(values)
          messages.forEach((message) => notification.success({ message }))
        }
        await refreshTableData()
        closeAmendmentPopup()
      }
    },
    [
      editMode,
      currentFile,
      amendmentDocument,
      uploadHSAmendmentDocumentAsync,
      selectedHSRowKey,
      canAddFacilityLoPSignatories,
      LoPSignatureRequired,
      pricingEffectiveDate,
      selectedHSRow.amendment_document_uuid,
      selectedHSRow.hospital_level,
      selectedHSRow.hs_uuid,
      selectedHSRow.uuid,
      refreshTableData,
      closeAmendmentPopup,
      addAmendmentDocumentAsync,
      getReassignmentMessages
    ]
  )

  useEffect(() => {
    // predefine data
    if (selectedHSRow) {
      if (editMode) {
        setCurrentFile(selectedHSRow.amendment_document)
        if (selectedHSRow.pricing_effective_date) {
          form.setFieldsValue({
            pricing_effective_date: moment(selectedHSRow.pricing_effective_date)
          })
        }
        if (selectedHSRow.initial_calculation_date) {
          form.setFieldsValue({
            initial_calculation_date: moment(
              selectedHSRow.initial_calculation_date
            )
          })
        }
        if (
          selectedHSRow.vendor_signatory &&
          selectedHSRow.community_signatory &&
          selectedHSRow.is_signature_required
        ) {
          form.setFieldsValue({
            is_signature_required: true,
            vendor_signatory:
              selectedHSRow.vendor_signatory.signatory?.uuid ??
              selectedHSRow.vendor_signatory.email ??
              undefined,
            community_signatory:
              selectedHSRow.community_signatory.signatory?.uuid ??
              selectedHSRow.community_signatory.email ??
              undefined,
            vendor_signatory_phone_number: selectedHSRow.vendor_signatory
              ?.phone_number
              ? selectedHSRow.vendor_signatory.phone_number.substring(2)
              : '',
            community_signatory_phone_number: selectedHSRow.community_signatory
              .phone_number
              ? selectedHSRow.community_signatory.phone_number.substring(2)
              : ''
          })
        } else {
          form.setFieldsValue({
            is_signature_required: false
          })
        }
      } else if (!editMode && details.start_date) {
        form.setFieldsValue({
          pricing_effective_date: moment(details.start_date),
          initial_calculation_date: handleInitialCalculationDate(
            moment(details.start_date)
          )
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHSRow, editMode, setCurrentFile, form])

  const onPricingDateValueChange = useCallback(
    (date: Moment | null) => {
      if (date && date.isAfter(moment(initialCalculationDate))) {
        form.setFieldsValue({
          initial_calculation_date: handleInitialCalculationDate(date.clone())
        })
      }
    },
    [form, initialCalculationDate, handleInitialCalculationDate]
  )

  const UploadDocumentField = useMemo(() => {
    if (currentFile)
      return (
        <Attachment
          className="add-amendment-form__attachment half-width"
          link={currentFile}
          filename={fileNameFormatter(currentFile)}
          onDelete={handleDeleteDocument}
        />
      )
    else if (amendmentDocument)
      return (
        <Attachment
          className="add-amendment-form__attachment half-width"
          link={URL.createObjectURL(amendmentDocument?.file)}
          filename={amendmentDocument.file.name}
          onDelete={handleDeleteDocument}
        />
      )
    else
      return (
        <FileUpload
          documentType="attachments"
          uploadBtnText={BTN_TXT.UPLOAD_LOP}
          handleUploadFile={handleUploadDocument}
          uploadFilesProps={{
            ...UPLOAD_DOCUMENTS_CONFIG,
            onDropAccepted: handleUploadDocument,
            validator: (file) =>
              validateFileTypes(
                ACCEPT_DOCUMENT_TYPES,
                file,
                MAX_FILE_SIZE_30_MB
              )
          }}
        />
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    amendmentDocument,
    handleDeleteDocument,
    handleUploadDocument,
    selectedHSRow,
    editMode,
    currentFile
  ])

  return (
    <CustomForm<TAddAmendmentForm>
      form={form}
      noValidate={true}
      onFinish={handleFinishForm}
      id="add-amendment-form-id"
      validateMessages={{
        ...validateMessages,
        types: {
          email: 'Enter a valid email'
        }
      }}
      className={cn(
        'add-signatory-form add-edit-event-calendar-form two-columns'
      )}
    >
      {!editMode && (
        <Typography.Body1>
          To add Health System to the contract, please upload LOP and provide
          pricing effective date.
        </Typography.Body1>
      )}
      <div className="mt-24 mb-24 gap-16">
        <div className="row two-columns mb-24">
          <Datepicker
            propsItem={{
              name: 'pricing_effective_date',
              label: LABELS.PRICING_EFFECTIVE_DATE
            }}
            propsDate={{
              // this required for popup render outside the modal window
              // do not remove this line of code
              getPopupContainer: (node) => document?.body ?? node,
              // ---
              disabled: !details.start_date,
              disabledDate: checkDateRange,
              placeholder: 'mm/dd/yyyy',
              value: pricingEffectiveDate,
              name: 'pricing_effective_date',
              onChange: onPricingDateValueChange
            }}
          />
          <Datepicker
            propsItem={{
              name: 'initial_calculation_date',
              label: LABELS.INITIAL_CALCULATION_DATE
            }}
            propsDate={{
              // this required for popup render outside the modal window
              // do not remove this line of code
              getPopupContainer: (node) => document?.body ?? node,
              // ---
              disabled: !details.start_date || !pricingEffectiveDate,
              defaultValue:
                editMode && selectedHSRow.initial_calculation_date
                  ? moment(selectedHSRow.initial_calculation_date)
                  : undefined,
              disabledDate: checkDateRangeForInitial,
              format: [timelineMonthYearFormat],
              placeholder: 'mm/yyyy',
              name: 'initial_calculation_date',
              picker: 'month',
              superNextIcon: <ArrowRight16 />,
              superPrevIcon: <ArrowLeft16 />,
              onChange: onChangeInitialCalculationDate
            }}
          />
        </div>
        <div className="row vertical mt-0">
          <Typography.Label
            className="add-amendment-form__lop-label"
            weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}
          >
            Letter of Participation
          </Typography.Label>
          {UploadDocumentField}
        </div>
      </div>
      {canAddFacilityLoPSignatories && (
        <div className="row gap-12 mb-24">
          <Tooltip title={TOOLTIP.LOP_SIGNATURE_REQUIRED}>
            <Form.Item
              name="is_signature_required"
              valuePropName="checked"
              style={{ width: 36 }}
            >
              <Switch
                disabled={
                  !!selectedHSRow.vendor_signatory?.status ||
                  !!selectedHSRow.community_signatory?.status
                    ? !isDocWasUpdated
                    : false
                }
              />
            </Form.Item>
          </Tooltip>
          <Typography.Body1>LoP signature required</Typography.Body1>
        </div>
      )}
      {canAddFacilityLoPSignatories && LoPSignatureRequired && (
        <>
          <Typography.Headline6>Signatories</Typography.Headline6>
          <div className="row mt-16 gap-16">
            <AddSignatorySelect
              form={form}
              selectPropsItem={{
                label: LABELS.VENDOR_SIGNATORY,
                name: 'vendor_signatory'
              }}
              propsSelect={{
                disabled:
                  selectedHSRow.vendor_signatory?.status ===
                  SIGNATORY_STATUS.SIGNED
                    ? !isDocWasUpdated
                    : false
              }}
              className="half-width"
              optionsRequest={() => {
                if (selectedHSRow.hospital_level && selectedHSRow.hs_uuid) {
                  return getAvailableVendorUsersForHospitalSignatory(
                    id ?? '',
                    selectedHSRow.uuid
                  )
                }
                return getAvailableVendorUsersForSignatory(
                  id ?? '',
                  selectedHSRowKey.toString()
                )
              }}
              setOptionsToParent={setVendorSignatories}
              phoneNumberFieldName="vendor_signatory_phone_number"
            />
            <AddSignatorySelect
              form={form}
              selectPropsItem={{
                label: LABELS.HS_SIGNATORY,
                name: 'community_signatory'
              }}
              propsSelect={{
                disabled:
                  selectedHSRow.community_signatory?.status ===
                  SIGNATORY_STATUS.SIGNED
                    ? !isDocWasUpdated
                    : false
              }}
              className="half-width"
              phoneNumberFieldName="community_signatory_phone_number"
              optionsRequest={() => {
                if (selectedHSRow.hospital_level && selectedHSRow.hs_uuid) {
                  return getAvailableHSUsersForHospitalSignatory(
                    id ?? '',
                    selectedHSRow.uuid
                  )
                }
                return getAvailableHSUsersForSignatory(
                  id ?? '',
                  selectedHSRowKey.toString()
                )
              }}
              setOptionsToParent={setHsSignatories}
            />
          </div>
        </>
      )}
    </CustomForm>
  )
}
